import { createWithEqualityFn } from "zustand/traditional";
import { createJSONStorage, persist } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { isWeb } from "@companion-core/shared/app/Utils/platform";

interface AutoLogoutState {
  timeoutId: ReturnType<typeof setTimeout> | -1;
  sessionExpiresAt: number;
}

interface AutoLogoutActions {
  initTimeout: (sessionExpiresAt: number, rehydrate: () => Promise<void> | void) => void;
}

export const initialState: AutoLogoutState = {
  timeoutId: -1,
  sessionExpiresAt: -1,
};

const initTimeout = (
  delay: number,
  rehydrate: () => Promise<void> | void,
  get: () => AutoLogoutState & AutoLogoutActions
): ReturnType<typeof setTimeout> => {
  return setTimeout(async () => {
    rehydrate();
    if (useAuthStore.getState().authenticated && get().sessionExpiresAt <= Date.now()) {
      await useAuthStore.getState().logout();
    } else if (useAuthStore.getState().authenticated) {
      initTimeout(get().sessionExpiresAt - Date.now(), rehydrate, get);
    }
  }, delay);
};

export const useAutoLogoutStore = createWithEqualityFn<AutoLogoutState & AutoLogoutActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      initTimeout: (sessionExpiresAt: number, rehydrate: () => Promise<void> | void) => {
        set({ sessionExpiresAt });
        const delay = sessionExpiresAt - Date.now();
        const currentTimeoutId = get().timeoutId;
        if (
          currentTimeoutId !== -1 &&
          currentTimeoutId !== null &&
          currentTimeoutId !== undefined
        ) {
          try {
            clearTimeout(currentTimeoutId);
          } catch {
            // Ignore clearTimeout errors
          }
        }

        const id: ReturnType<typeof setTimeout> = initTimeout(delay, rehydrate, get);
        set({ timeoutId: id });
      },
    }),
    {
      name: "autoLogout-storage",
      storage: createJSONStorage(() => (isWeb() ? localStorage : AsyncStorage)),
      partialize: (state) => ({
        sessionExpiresAt: state.sessionExpiresAt,
      }),
    }
  )
);
